/**
 * AssignmentEditableValuePill — inline-editable value text for an UPDATE
 * `col = value` assignment. Mirrors `FilterEditablePill` and `JoinEditableOnPill`
 * — click to enter edit mode, Enter / blur commits, Escape reverts. Empty /
 * unchanged short-circuits to no commit. The input suggests columns and
 * functions as you type (`SqlValueInput`).
 *
 * A trailing comment (`1 --ar applied`) is not part of the value: it shows
 * after it as a comment (`ValueComment`), stays visible while editing, and the
 * value edit leaves it in the SQL (the planners stop the value before it).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.
 */

import type { ConflictTailTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { VqeTooltip } from '../common/VqeTooltip';
import { modifyUpdateAssignment } from '../../edit/edits/modifyUpdateAssignment';
import { modifyInsertConflictAssignment } from '../../edit/edits/insertConflictAssignments';
import {
  modifyMergeUpdateAssignment,
  type MergeBranchSelector,
} from '../../edit/edits/modifyMergeBranch';
import { recordEditFailure, safeExtractStructure } from '../insert/insertColumnMapHelpers';
import { escapeClaimProps } from '../../../common/utils/escapeClaimTarget';
import { EditableSqlValue } from '../../../common/components/EditableSqlValue';
import type { Column } from '../../../../sql-resolver/types/schema';
import { vqeCopy } from '../../ux/vqeCopy';
import { columnValueSuggestions } from '../../../common/utils/sqlValueSuggestions';
import type { WrittenTableColumns } from './assignmentColumns';

export interface AssignmentEditableValuePillProps {
  readonly testId: string;
  readonly text: string;
  readonly index: number;
  readonly editContext: VqeEditContext;
  /**
   * When supplied, edits route through `modify-merge-update-assignment`
   * targeting the picked MERGE branch's nested SET list instead of the
   * top-level UPDATE statement.
   */
  readonly mergeBranch?: MergeBranchSelector;
  /**
   * When supplied, edits route through `modify-insert-conflict-assignment`
   * targeting an INSERT structured tail (`'on-conflict'` → ON CONFLICT DO
   * UPDATE SET …, `'on-duplicate'` → ON DUPLICATE KEY UPDATE …) — the third
   * router beside `mergeBranch` (§2.4 S6b).
   */
  readonly conflictTail?: ConflictTailTarget;
  /** The value's trailing comment, shown after it and kept on edit. */
  readonly comment?: string;
  /** The written table and target column — the input's suggestions. */
  readonly table?: WrittenTableColumns | null;
  readonly column?: Column;
}

/** A value's trailing SQL comment, marked as one: muted italic, with a tooltip. */
export function ValueComment({ text, testId }: { readonly text: string; readonly testId: string }) {
  if (!text) return null;
  return (
    <VqeTooltip content={vqeCopy.dmlColumns.commentHint}>
      <span className="min-w-0 truncate pr-0.5 font-mono text-xs italic text-muted-foreground" data-testid={testId}>
        {text}
      </span>
    </VqeTooltip>
  );
}

export function AssignmentEditableValuePill({
  testId,
  text,
  index,
  editContext,
  mergeBranch,
  conflictTail,
  comment = '',
  table,
  column,
}: AssignmentEditableValuePillProps) {
  const commit = (value: string): void => {
    const ctx = toCommitCtx(editContext);
    if (conflictTail) {
      modifyInsertConflictAssignment(index, value, conflictTail, ctx, {
        structure: safeExtractStructure(editContext),
      }).catch(recordEditFailure);
    } else if (mergeBranch) {
      void modifyMergeUpdateAssignment(mergeBranch, index, value, ctx);
    } else {
      void modifyUpdateAssignment(index, value, ctx);
    }
  };
  // The `escapeClaimProps` marker lets `CompactVqePopover`'s `onEscapeKeyDown`
  // claim Escape for the input while it's mounted (F1 — Escape-in-popover
  // seam consolidation) so Escape cancels the edit only, never the ancestor
  // popover; inert (and harmless) outside a compact-popover host.
  return (
    <EditableSqlValue
      text={text}
      onCommit={commit}
      getSuggestions={table ? () => columnValueSuggestions(table.engine, table.columns, column) : undefined}
      testId={testId}
      inputTestId={`${testId}-input`}
      ariaLabel="Edit assignment value"
      className="min-w-0 whitespace-pre-wrap break-words text-left font-mono text-xs hover:underline"
      inputClassName="min-w-0 flex-1 py-0.5"
      inputProps={escapeClaimProps}
      wrapDisplay={(button) => <VqeTooltip content="Click to edit value">{button}</VqeTooltip>}
      wrapperClassName="flex min-w-0 flex-1 items-center gap-2"
      trailing={<ValueComment text={comment} testId={`${testId}-comment`} />}
      trackEvent={'feature:vqe_value_suggestion_accept'}
    />
  );
}
